import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Briefcase, 
  ArrowRight,
  Clock,
  CheckCircle,
  Target,
  Lightbulb,
  AlertCircle
} from "lucide-react";

const TellMeAboutYourselfPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-primary" />
              <span className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Hireun
              </span>
            </Link>
            <Link to="/">
              <Button data-testid="get-started-btn">
                Get Personalized Answer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-accent/50 to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="secondary" className="mb-4">Interview Guide</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How to Answer "Tell Me About Yourself"
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Master the most common interview opener with a structured, compelling answer that sets the tone for success.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Why It Matters */}
        <Card className="mb-12">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why This Question Matters
            </h2>
            <p className="text-muted-foreground mb-6">
              "Tell me about yourself" is often the first question in an interview. Your answer sets the tone for the entire conversation and creates the first impression. A strong response can:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                "Establish your professional narrative",
                "Highlight relevant experience",
                "Show confidence and preparation",
                "Guide the interview toward your strengths"
              ].map((point, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* The Formula */}
        <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          The Perfect Formula
        </h2>
        
        <div className="space-y-6 mb-12">
          {[
            {
              step: 1,
              title: "Present",
              time: "30 seconds",
              description: "Start with your current role and what you do. Keep it focused on your professional identity.",
              example: "I'm currently a Senior Product Manager at TechCorp, where I lead a team of 8 and oversee our B2B SaaS platform."
            },
            {
              step: 2,
              title: "Past",
              time: "45 seconds",
              description: "Connect your background to show progression and relevant experience.",
              example: "Before this, I spent 4 years at StartupXYZ, starting as a Product Analyst and growing into a PM role where I launched 3 successful products."
            },
            {
              step: 3,
              title: "Future",
              time: "15 seconds",
              description: "Tie it to why you're here and what excites you about this opportunity.",
              example: "I'm excited about this role at [Company] because it combines my passion for user-centric design with your mission to revolutionize healthcare."
            }
          ].map(({ step, title, time, description, example }) => (
            <Card key={step} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="flex">
                  <div className="bg-primary text-white p-6 flex flex-col items-center justify-center min-w-[100px]">
                    <span className="text-3xl font-bold">{step}</span>
                    <span className="text-sm opacity-80">{title}</span>
                  </div>
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{time}</span>
                    </div>
                    <p className="mb-4">{description}</p>
                    <div className="bg-secondary/50 p-4 rounded-lg">
                      <p className="text-sm italic">"{example}"</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tips */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Do</h3>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Keep it to 60-90 seconds
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Tailor it to the specific role
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Include specific achievements
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Show enthusiasm for the opportunity
                </li>
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Don't</h3>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0">✕</span>
                  Recite your entire resume
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0">✕</span>
                  Share personal life details
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0">✕</span>
                  Speak for more than 2 minutes
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0">✕</span>
                  Give a generic, one-size-fits-all answer
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <Card className="bg-primary text-white mb-12">
          <CardContent className="p-8 text-center">
            <Target className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Get Your Personalized Answer
            </h2>
            <p className="opacity-90 mb-6 max-w-lg mx-auto">
              Upload your CV and paste the job description. Our AI will create a "Tell me about yourself" answer tailored to your background and the specific role.
            </p>
            <Link to="/">
              <Button variant="secondary" size="lg" data-testid="cta-personalized">
                Create My Personalized Answer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Briefcase className="w-5 h-5" />
            <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Hireun
            </span>
          </div>
          <p className="text-sm text-gray-400">
            &copy; 2025 Hireun. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TellMeAboutYourselfPage;
